import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { getFichajes, getSolicitudesAprobadasUsuario } from "../../api/api";

/**
 * Componente de calendario mensual de fichajes.
 * Muestra los fichajes y solicitudes aprobadas del usuario en formato de calendario.
 * Incluye indicadores visuales para entradas, salidas, ausencias y vacaciones.
 * 
 * @returns {JSX.Element} Calendario interactivo con fichajes y solicitudes
 */
export default function CalendarioFichajes() {
  const { token, user } = useContext(AuthContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [fichajes, setFichajes] = useState([]);
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  // Cargar fichajes del mes actual
  const cargarFichajesMes = async (fecha) => {
    try {
      setLoading(true);
      const anio = fecha.getFullYear();
      const mes = fecha.getMonth() + 1;
      
      const query = `?anio=${anio}&mes=${mes}`;
      const data = await getFichajes(token, query);
      
      // Convertir a array si es un objeto pseudo-array
      let dataArray;
      if (Array.isArray(data)) {
        dataArray = data;
      } else if (data && typeof data === 'object') {
        const keys = Object.keys(data).filter(k => !isNaN(parseInt(k)));
        dataArray = keys.map(k => data[k]);
      } else {
        dataArray = [];
      }
      
      // Filtrar solo los fichajes de este usuario
      const misFichajes = dataArray.filter((f) => f.userId?._id === user._id);
      setFichajes(misFichajes);
    } catch (err) {
      console.error('❌ Error al cargar fichajes del calendario:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar solicitudes aprobadas del usuario
  const cargarSolicitudesAprobadas = async () => {
    try {
      const data = await getSolicitudesAprobadasUsuario(token);
      setSolicitudes(data);
    } catch (err) {
    }
  };

  useEffect(() => {
    if (user?._id) {
      cargarFichajesMes(currentDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, user?._id, user?.nombre]); // Agregado user para recargar cuando cambie

  useEffect(() => {
    if (user?._id) {
      cargarSolicitudesAprobadas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, user?.nombre]); // Agregado user para recargar cuando cambie

  // Obtener el primer día del mes
  const getPrimerDiaMes = () => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  };

  // Obtener el último día del mes
  const getUltimoDiaMes = () => {
    return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  };

  // Obtener el día de la semana del primer día (0 = domingo, 1 = lunes, etc.)
  const getDiaSemanaInicio = () => {
    const dia = getPrimerDiaMes().getDay();
    return dia === 0 ? 6 : dia - 1; // Ajustar para que lunes sea 0
  };

  // Generar los días del calendario
  const generarDiasCalendario = () => {
    const primerDia = getPrimerDiaMes();
    const ultimoDia = getUltimoDiaMes();
    const diasMes = ultimoDia.getDate();
    const diasSemanaInicio = getDiaSemanaInicio();
    
    const dias = [];
    
    // Días vacíos al inicio
    for (let i = 0; i < diasSemanaInicio; i++) {
      dias.push(null);
    }
    
    // Días del mes
    for (let dia = 1; dia <= diasMes; dia++) {
      dias.push(dia);
    }
    
    return dias;
  };

  // Obtener estadísticas de un día
  const getEstadisticasDia = (dia) => {
    if (!dia) return null;
    
    const fechaDia = new Date(currentDate.getFullYear(), currentDate.getMonth(), dia);
    const fichajesDia = fichajes.filter((f) => {
      const fechaFichaje = new Date(f.fecha);
      return (
        fechaFichaje.getDate() === dia &&
        fechaFichaje.getMonth() === currentDate.getMonth() &&
        fechaFichaje.getFullYear() === currentDate.getFullYear()
      );
    });

    const entradas = fichajesDia.filter((f) => f.tipo === "entrada").length;
    const salidas = fichajesDia.filter((f) => f.tipo === "salida").length;
    const ausencias = fichajesDia.filter((f) => f.tipo === "ausencia").length;

    return {
      total: fichajesDia.length,
      entradas,
      salidas,
      ausencias,
      fichajes: fichajesDia,
      completo: entradas > 0 && salidas > 0,
      incompleto: entradas > 0 && salidas === 0,
      ausente: ausencias > 0,
    };
  };

  // Obtener clase CSS según estado del día
  const getClaseDia = (dia) => {
    if (!dia) return "";
    const hoy = new Date();
    const esHoy =
      dia === hoy.getDate() &&
      currentDate.getMonth() === hoy.getMonth() &&
      currentDate.getFullYear() === hoy.getFullYear();

    const stats = getEstadisticasDia(dia);
    let clase = "relative p-2 min-h-[70px] border border-gray-200 cursor-pointer transition ";
    if (esHoy) {
      clase += "border-2 border-blue-600 ";
    }
    // Prioridad: fichaje > solicitud aprobada
    if (stats && stats.total > 0) {
      if (stats.ausente) {
        clase += "bg-red-200 hover:bg-red-300 text-gray-800 ";
      } else if (stats.completo) {
        clase += "bg-green-200 hover:bg-green-300 text-gray-800 ";
      } else if (stats.incompleto) {
        clase += "bg-orange-200 hover:bg-orange-300 text-gray-800 ";
      }
    } else {
      // Si no hay fichaje, mirar si hay solicitud aprobada
      const fechaDia = new Date(currentDate.getFullYear(), currentDate.getMonth(), dia);
      let colorSolicitud = null;
      for (const solicitud of solicitudes) {
        const inicio = new Date(solicitud.fechaInicio);
        const fin = new Date(solicitud.fechaFin);
        if (
          fechaDia >= new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()) &&
          fechaDia <= new Date(fin.getFullYear(), fin.getMonth(), fin.getDate())
        ) {
          colorSolicitud = solicitud.tipo === "vacaciones" ? "azul" : "amarillo";
          break;
        }
      }
      if (colorSolicitud === "azul") {
        clase += "bg-blue-200 hover:bg-blue-300 text-gray-800 ";
      } else if (colorSolicitud === "amarillo") {
        clase += "bg-yellow-100 hover:bg-yellow-200 text-gray-800 ";
      } else {
        clase += "hover:bg-gray-100 ";
      }
    }
    return clase;
  };

  // Navegar al mes anterior
  const mesAnterior = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setHoveredDay(null);
  };

  // Navegar al mes siguiente
  const mesSiguiente = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setHoveredDay(null);
  };

  // Ir al mes actual
  const irHoy = () => {
    setCurrentDate(new Date());
    setHoveredDay(null);
  };

  // Manejar hover sobre un día
  const handleMouseEnter = (dia, event) => {
    if (!dia) return;
    const stats = getEstadisticasDia(dia);
    // Permitir hover si hay fichaje o si hay solicitud aprobada
    let tieneSolicitud = false;
    const fechaDia = new Date(currentDate.getFullYear(), currentDate.getMonth(), dia);
    for (const solicitud of solicitudes) {
      const inicio = new Date(solicitud.fechaInicio);
      const fin = new Date(solicitud.fechaFin);
      if (
        fechaDia >= new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()) &&
        fechaDia <= new Date(fin.getFullYear(), fin.getMonth(), fin.getDate())
      ) {
        tieneSolicitud = true;
        break;
      }
    }
    if ((stats && stats.total > 0) || tieneSolicitud) {
      const rect = event.currentTarget.getBoundingClientRect();
      setHoverPosition({ x: rect.left, y: rect.bottom });
      setHoveredDay(dia);
    }
  };

  const handleMouseLeave = () => {
    setHoveredDay(null);
  };

  const dias = generarDiasCalendario();
  const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  return (
    <div className="p-6">
      {/* Header del calendario */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">
            Calendario de Fichajes
          </h3>
          <button
            onClick={irHoy}
            className="bg-gray-200 text-gray-700 px-3 py-2 rounded hover:bg-gray-300 transition text-sm"
          >
            Hoy
          </button>
        </div>

        {/* Navegación de mes */}
        <div className="flex items-center justify-between bg-gray-100 rounded p-4">
          <button
            onClick={mesAnterior}
            className="w-8 h-8 bg-white rounded hover:bg-gray-200 transition flex items-center justify-center"
          >
            <i className="fas fa-chevron-left text-gray-600"></i>
          </button>
          
          <div className="text-center">
            <h4 className="text-lg font-semibold text-gray-700">
              {meses[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h4>
          </div>
          
          <button
            onClick={mesSiguiente}
            className="w-8 h-8 bg-white rounded hover:bg-gray-200 transition flex items-center justify-center"
          >
            <i className="fas fa-chevron-right text-gray-600"></i>
          </button>
        </div>
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b border-gray-200 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-200 border border-green-400 rounded"></div>
          <span className="text-gray-600">Completo</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-orange-200 border border-orange-400 rounded"></div>
          <span className="text-gray-600">Incompleto</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-200 border border-red-400 rounded"></div>
          <span className="text-gray-600">Ausencia (fichaje)</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-200 border border-blue-400 rounded"></div>
          <span className="text-gray-600">Vacaciones aprobadas</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded"></div>
          <span className="text-gray-600">Ausencia aprobada</span>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <i className="fas fa-spinner fa-spin text-3xl text-indigo-500 mb-2"></i>
          <p className="text-gray-600">Cargando calendario...</p>
        </div>
      )}

      {!loading && (
        <>
          {/* Grid del calendario */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {/* Encabezados de días */}
            {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((dia) => (
              <div
                key={dia}
                className="text-center font-semibold text-gray-600 py-2 bg-gray-50 text-sm"
              >
                {dia}
              </div>
            ))}

            {/* Días del mes */}
            {dias.map((dia, index) => {
              const stats = getEstadisticasDia(dia);
              return (
                <div
                  key={index}
                  className={getClaseDia(dia)}
                  onMouseEnter={(e) => handleMouseEnter(dia, e)}
                  onMouseLeave={handleMouseLeave}
                >
                  {dia && (
                    <div className="font-semibold text-sm flex items-center justify-center h-full">
                      {dia}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Tooltip con detalles del día */}
      {hoveredDay && (() => {
        const stats = getEstadisticasDia(hoveredDay);
        const fechaDia = new Date(currentDate.getFullYear(), currentDate.getMonth(), hoveredDay);
        // Si hay fichaje, mostrar tooltip de fichaje
        if (stats && stats.total > 0) {
          return (
            <div 
              className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[250px]"
              style={{
                left: `${hoverPosition.x}px`,
                top: `${hoverPosition.y + 5}px`,
                pointerEvents: 'none'
              }}
            >
              <h4 className="text-sm font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
                Fichajes del día {hoveredDay}
              </h4>
              <div className="space-y-2">
                {stats.fichajes.map((fichaje) => (
                  <div key={fichaje._id} className="flex items-center gap-2 text-sm">
                    <i className={`fas ${
                      fichaje.tipo === "entrada" ? "fa-sign-in-alt text-green-600" :
                      fichaje.tipo === "salida" ? "fa-sign-out-alt text-red-600" :
                      "fa-ban text-orange-600"
                    }`}></i>
                    <span className="capitalize font-medium text-gray-700">{fichaje.tipo}:</span>
                    <span className="text-gray-600">
                      {new Date(fichaje.fecha).toLocaleTimeString("es-ES", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        // Si no hay fichaje, pero hay solicitud aprobada, mostrar tooltip de solicitud
        let solicitudDia = null;
        for (const solicitud of solicitudes) {
          const inicio = new Date(solicitud.fechaInicio);
          const fin = new Date(solicitud.fechaFin);
          if (
            fechaDia >= new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()) &&
            fechaDia <= new Date(fin.getFullYear(), fin.getMonth(), fin.getDate())
          ) {
            solicitudDia = solicitud;
            break;
          }
        }
        if (solicitudDia) {
          return (
            <div 
              className="fixed z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[250px]"
              style={{
                left: `${hoverPosition.x}px`,
                top: `${hoverPosition.y + 5}px`,
                pointerEvents: 'none'
              }}
            >
              <h4 className="text-sm font-bold text-gray-800 mb-3 border-b border-gray-200 pb-2">
                {solicitudDia.tipo === "vacaciones" ? "Vacaciones aprobadas" : "Ausencia aprobada"} ({hoveredDay} {meses[currentDate.getMonth()]})
              </h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="capitalize font-medium text-gray-700">{solicitudDia.tipo}</span>
                  <span className="text-gray-600">
                    {`Del ${new Date(solicitudDia.fechaInicio).toLocaleDateString()} al ${new Date(solicitudDia.fechaFin).toLocaleDateString()}`}
                  </span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}
    </div>
  );
}
